import React from 'react';
import './StupidSystemDesigns.css';

const mazeSolverDesignCode = String.raw`findPathDFS(board):
    start = board.getStartCell()

    if start does not exist:
        return []

    visited = matrix(rows, cols, false)
    path = []
    visited[start] = true

    if dfs(start, board, visited, path):
        return path

    return []

dfs(cell, board, visited, path):
    if board[cell] == EXIT:
        return true

    for each direction in [UP, DOWN, LEFT, RIGHT]:
        next = move(cell, direction)

        if next is outside board:
            continue

        if board[next] == WALL or visited[next]:
            continue

        visited[next] = true
        path.add(direction)

        if dfs(next, board, visited, path):
            return true

        path.removeLast()

    return false

findPathBFS(board, start, end):
    queue = [start]
    visited[start] = true
    parent[start] = NONE

    while queue is not empty:
        current = queue.popFront()

        if current == end:
            return reconstructPath(parent, start, end)

        for each direction in [UP, DOWN, LEFT, RIGHT]:
            next = move(current, direction)

            if next is invalid or next is a wall:
                continue

            if visited[next]:
                continue

            visited[next] = true
            parent[next] = current
            parentDirection[next] = direction
            queue.pushBack(next)

    return []

reconstructPath(parent, start, end):
    path = []
    current = end

    while current != start:
        path.add(parentDirection[current])
        current = parent[current]

    reverse(path)
    return path`;

function MazeSolverDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Maze Solver &amp; Route Planner LLD</h1>
                            <p className="system-designs-subtitle">
                                ..and any other path finding issues, similar to uber shortest path finding. BUT note that those use variations of Djikstras algo (weighted graphs), ours is a simple unweighted graph.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Accept an m × n board and a starting position.</li>
                                <li>Cell values are 0 for free, 1 for a wall, 2 for the start, and 3 for an exit.</li>
                                <li>Find a valid path from the starting position to any exit.</li>
                                <li>Return a list of directions, or an empty list if no path exists.</li>
                                <li>Support multiple pathfinding algorithms in the future.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Core Entities</h2>
                            <ul>
                                <li><strong>MazeService:</strong> The entry point for solving mazes; exposes <code>getPath(board)</code>.</li>
                                <li>
                                    <strong>Board:</strong> Stores <code>grid: Matrix</code> and its row and column counts.
                                    It provides <code>isValid(row, col)</code>, <code>isWalkable(row, col)</code>, and <code>getStartCell()</code>.
                                </li>
                                <li>
                                    <strong>PathService:</strong> Implements pathfinding behind a common interface.
                                    <code>findPathDFS(board)</code> finds any valid path; <code>findPathBFS(board)</code> finds a shortest path
                                    when every move has equal cost.
                                </li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Maze Solver: DFS</h2>
                            <p className="system-design-description">
                                Depth-first search finds any valid path to an exit, not necessarily the shortest.
                                It tries each direction, skips walls and visited cells, and backtracks at dead ends.
                            </p>
                            <p className="system-design-description">
                                Time complexity is O(m × n). Space complexity is O(m × n) for the visited matrix and recursion stack,
                                excluding the returned path.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Route Planner: BFS</h2>
                            <p className="system-design-description">
                                Breadth-first search explores cells level by level. For equal-cost moves, the first time it reaches the
                                destination it has found a shortest path. The planner accepts a board, start cell, and destination, and
                                returns directions or an empty list if no path exists.
                            </p>
                            <p className="system-design-description">
                                Time complexity is O(m × n). Space complexity is O(m × n) for the queue, visited matrix, and parent information.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Pseudocode</h2>
                            <pre className="design-code-block"><code>{mazeSolverDesignCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <h2>Path Reconstruction</h2>
                            <p className="system-design-description">
                                BFS records the parent cell and direction whenever it discovers a cell. Starting at the destination,
                                follow parent pointers backward, collect each stored direction, then reverse the list to get the route
                                from start to destination.
                            </p>
                            <pre className="design-code-block"><code>{`parent[B] = A
parent[C] = B
parent[D] = C

parentDirection[B] = RIGHT
parentDirection[C] = DOWN
parentDirection[D] = RIGHT`}</code></pre>
                        </div>

                        <div className="design-section">
                            <h2>Design Decisions</h2>
                            <ul>
                                <li><strong>MazeService:</strong> Accepts input and returns a path.</li>
                                <li><strong>Board:</strong> Stores the grid and validates cell positions.</li>
                                <li><strong>PathService:</strong> Implements pathfinding algorithms.</li>
                                <li><strong>DFS:</strong> Finds any valid path to an exit.</li>
                                <li><strong>BFS:</strong> Finds the shortest path when every move has equal cost.</li>
                                <li><strong>Path reconstruction:</strong> Converts parent pointers into an ordered list of directions.</li>
                            </ul>
                            <p className="system-design-description">
                                <strong>Core design principle:</strong> Keep the board representation separate from pathfinding.
                                This lets DFS, BFS, and future algorithms such as Dijkstra&apos;s algorithm or A* share the same board abstraction.
                            </p>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        Maybe i should make a new section called "Sankalp's algos" which is just variations of common algos but with the worst possible time & space complexity. Ooh. OOH YES!
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default MazeSolverDesign;
